import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Droplets } from 'lucide-react';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { MarketStartPriceEditor } from './MarketStartPriceEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v, d = 2) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: d });

export const MarketAddFundsForm = ({ token, state, busy, onStarted, onReload }) => {
  const [usdc, setUsdc] = useState('');
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };
  const w = state?.wallet || {};
  const short = plan && (w.usdc < plan.usdc_amount || w.beth < plan.beth_amount || w.eth < plan.network_fee_eth);

  const preview = async () => {
    setLoading(true); setPlan(null);
    try {
      const r = await axios.get(`${API}/pas-admin/market/funding-plan`, { params: { usdc_amount: usdc }, headers });
      setPlan(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not prepare'); } finally { setLoading(false); }
  };

  const start = async () => {
    setConfirmOpen(false);
    try {
      const r = await axios.post(`${API}/pas-admin/market/add-funds`, { usdc_amount: parseFloat(usdc), confirm: true }, { headers });
      onStarted(r.data.job_id); setPlan(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start'); }
  };

  return (
    <div className="rounded-lg border border-slate-700 bg-slate-900/50 p-5 space-y-4" data-testid="market-add-funds-form">
      <h4 className="text-white font-semibold flex items-center gap-2"><Droplets className="w-4 h-4 text-sky-400" /> Add funds to the BETH/USDC pool</h4>
      <p className="text-xs text-slate-400">You put in USDC plus the matching BETH at the pool price ({state?.pool?.exists ? 'existing pool' : `new pool starts at $${fmt(state?.reference_usdc_per_beth, 6)} per BETH`}). Buyers can then swap against it.</p>
      {state && !state.pool?.exists && (
        <MarketStartPriceEditor token={token} current={state.reference_usdc_per_beth} onSaved={() => { setPlan(null); onReload(); }} />
      )}
      <label className="text-sm text-slate-300 space-y-1 block">USDC amount
        <input type="number" min="0" value={usdc} onChange={(e) => { setUsdc(e.target.value); setPlan(null); }} data-testid="market-add-usdc-input"
          className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white font-mono" />
      </label>
      <p className="text-xs text-slate-500">Wallet holds {fmt(w.usdc, 2)} USDC · {fmt(w.beth)} BETH · {fmt(w.eth, 6)} ETH for fees</p>
      {plan && (
        <div className="text-sm space-y-1 rounded bg-slate-800/70 p-3" data-testid="market-add-plan">
          <p className="text-white">Adds <strong>{fmt(plan.usdc_amount)} USDC</strong> + <strong>{fmt(plan.beth_amount, 6)} BETH</strong> at ${fmt(plan.price_usdc_per_beth, 6)} per BETH</p>
          <p className="text-slate-400 text-xs">{plan.creates_pool ? 'Creates the pool first. ' : ''}Network fee ≈ {fmt(plan.network_fee_eth, 6)} ETH</p>
          {plan.blocked_reason && <p className="text-red-400 text-xs" data-testid="market-add-blocked">{plan.blocked_reason}</p>}
          {short && <p className="text-red-400 text-xs" data-testid="market-add-short">The funding wallet doesn't hold enough USDC, BETH or ETH for this. Send USDC to {w.address} first.</p>}
        </div>
      )}
      <div className="flex gap-2">
        <button onClick={preview} disabled={!(usdc > 0) || loading} data-testid="market-add-preview-btn"
          className="px-4 py-2 text-sm rounded bg-slate-700 hover:bg-slate-600 disabled:bg-slate-700 disabled:text-slate-500 disabled:cursor-not-allowed text-white flex items-center gap-2">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />} Preview
        </button>
        <button onClick={() => setConfirmOpen(true)} disabled={!plan || short || !!plan?.blocked_reason || busy} data-testid="market-add-submit-btn"
          className="px-4 py-2 text-sm rounded bg-sky-500 hover:bg-sky-600 disabled:bg-slate-700 disabled:text-slate-500 disabled:cursor-not-allowed text-white">Add funds</button>
      </div>
      <ConfirmActionDialog open={confirmOpen} testId="market-add-confirm" title="Add funds to the pool" confirmLabel="Confirm"
        onCancel={() => setConfirmOpen(false)} onConfirm={start}
        rows={plan ? [['USDC', fmt(plan.usdc_amount)], ['BETH', fmt(plan.beth_amount, 6)], ['Price', `$${fmt(plan.price_usdc_per_beth, 6)} per BETH`],
          ['Creates pool', plan.creates_pool ? 'Yes' : 'No'], ['Network fee', `≈ ${fmt(plan.network_fee_eth, 6)} ETH`]] : []} />
    </div>
  );
};

export default MarketAddFundsForm;
